/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Flex, Label, Text } from 'theme-ui';
import Button from '@/components/Button';

const COPIED_FEEDBACK_MS = 2000;

interface Props {
    label: string;
    required?: boolean;
    parseError: boolean;
    rawContent: React.ReactNode;
    treeContent: React.ReactNode;
    getCopyText: () => string | undefined;
    error?: React.ReactNode;
}

const JsonFieldViewer: React.FC<Props> = ({
    label,
    required,
    parseError,
    rawContent,
    treeContent,
    getCopyText,
    error,
}) => {
    const [showRaw, setShowRaw] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyRaw = () => {
        const text = getCopyText();
        if (text === undefined) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        });
    };

    return (
        <Box mb="2" sx={{ width: '100%' }}>
            <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Flex sx={{ gap: 2, alignItems: 'center' }}>
                    <Label sx={{ width: 'auto' }}>
                        {label}
                        {required && (
                            <Text color="danger" ml="1">
                                *
                            </Text>
                        )}
                    </Label>
                    <Button variant="secondary" type="button" onClick={() => setShowRaw((s) => !s)}>
                        {showRaw ? 'View Tree' : 'View Raw'}
                    </Button>
                    {showRaw && (
                        <Button
                            variant="secondary"
                            type="button"
                            disabled={parseError}
                            onClick={copyRaw}
                        >
                            {copied ? 'Copied!' : 'Copy'}
                        </Button>
                    )}
                </Flex>
            </Flex>
            {showRaw ? (
                rawContent
            ) : parseError ? (
                <Text color="danger">Invalid JSON — switch to Raw to fix it.</Text>
            ) : (
                treeContent
            )}
            {error}
        </Box>
    );
};

export default JsonFieldViewer;
